import { onDocumentReady, prefersReducedMotion } from '@theme/utilities';

const READY_CLASS = 'wateya-scroll-reveal-ready';
const REVEAL_CLASS = 'wateya-scroll-reveal';
const REVEALED_CLASS = 'is-revealed';
const PREPARED_ATTRIBUTE = 'data-wateya-scroll-reveal';
const EXCLUDED_SELECTOR = [
  'script',
  'style',
  'dialog',
  '[hidden]',
  '.sticky-add-to-cart',
  '.custom-product-template-hero__mobile-bar',
  '.custom-product-template-hero__zoom-dialog',
].join(',');

/** @type {IntersectionObserver | undefined} */
let observer;

/** @type {MutationObserver | undefined} */
let mutationObserver;

let mutationFrame = 0;
const pendingRoots = new Set();

/**
 * @param {string[]} selectors
 * @returns {string}
 */
function selectorList(selectors) {
  return selectors.join(',');
}

const homeRevealGroups = [
  {
    selector: selectorList([
      '.custom-index-hero__content > :not(.custom-index-hero__trust)',
      '.custom-index-hero__trust span',
    ]),
    type: 'copy',
    y: 14,
    stagger: 48,
    skipOnMobile: true,
  },
  {
    selector: '.custom-index-hero__media',
    type: 'media',
    y: 18,
    baseDelay: 120,
    skipOnMobile: true,
  },
  {
    selector: selectorList([
      '.custom-index-trust__card',
      '.custom-index-gallery__tile',
      '.custom-index-editorial__chips span',
      '.custom-index-rooms__card',
      '.custom-index-cta__card',
      '.custom-index-showcase__card',
      '.custom-index-showcase__feature-mobile-card:not(.custom-index-showcase__feature-mobile-card--hidden)',
      '.custom-index-showcase__routine-tab',
      '.custom-index-showcase__compare-card',
      '.custom-index-showcase__bundle-card',
      '.custom-index-showcase__community-card',
      '.custom-index-showcase__safety-card',
      '.custom-index-showcase__faq',
      '.custom-index-showcase__icon-card',
      '.custom-index-showcase__list-card',
    ]),
    type: 'card',
    y: 18,
    stagger: 54,
  },
  {
    selector: selectorList([
      '.custom-index-gallery__eyebrow',
      '.custom-index-gallery__heading',
      '.custom-index-gallery__caption',
      '.custom-index-editorial__eyebrow',
      '.custom-index-editorial__heading',
      '.custom-index-editorial__text',
      '.custom-index-rooms__eyebrow',
      '.custom-index-rooms__heading',
      '.custom-index-rooms__text',
      '.custom-index-cta__eyebrow',
      '.custom-index-cta__heading',
      '.custom-index-cta__text',
      '.custom-index-cta__button-row',
      '.custom-index-showcase__header',
      '.custom-index-showcase__eyebrow',
      '.custom-index-showcase__heading',
      '.custom-index-showcase__text',
      '.custom-index-showcase__actions',
      '.custom-index-showcase__note',
      '.custom-index-showcase__steps-note',
    ]),
    type: 'copy',
    y: 16,
    stagger: 44,
  },
  {
    selector: selectorList([
      '.custom-index-cta__image',
      '.custom-index-cta__form-wrap',
      '.custom-index-showcase__feature-media',
      '.custom-index-showcase__feature-overlay',
      '.custom-index-showcase__pills',
      '.custom-index-showcase__routine-shell',
      '.custom-index-showcase__table-wrap',
      '.custom-index-showcase__compare-mobile',
      '.custom-index-showcase__steps',
    ]),
    type: 'media',
    y: 22,
    stagger: 64,
  },
];

const productRevealGroups = [
  {
    selector: '.product-information__media',
    type: 'media',
    y: 18,
    stagger: 80,
  },
  {
    selector: selectorList([
      '.product-details .group-block-content > :not(.view-product-title)',
      '.product-recommendations .section-resource-list__content',
      '.product-recommendations .resource-list__item',
    ]),
    type: 'copy',
    y: 14,
    stagger: 48,
  },
  {
    selector: selectorList([
      '.custom-product-crumbs__inner',
      '.custom-product-template-hero__content > :not(.custom-product-template-hero__mobile-bar)',
      '.custom-product-template-use__header',
      '.custom-product-template-features__header',
      '.custom-product-panel__header',
      '.custom-product-panel__inner--center > :not(.custom-product-panel__cta-actions)',
      '.custom-product-template-faq__eyebrow',
      '.custom-product-template-faq__heading',
      '.custom-product-template-faq__text',
    ]),
    type: 'copy',
    y: 14,
    stagger: 46,
  },
  {
    selector: selectorList([
      '.custom-product-template-hero__gallery',
      '.custom-product-template-features__media',
      '.custom-product-template-features__overlay',
      '.custom-product-panel__image',
      '.custom-product-panel__table-wrap',
    ]),
    type: 'media',
    y: 20,
    stagger: 70,
  },
  {
    selector: selectorList([
      '.custom-product-template-hero__bullets li',
      '.custom-product-template-hero__thumb',
      '.custom-product-template-hero__trust span',
      '.custom-product-template-use__items span',
      '.custom-product-template-features__card',
      '.custom-product-panel__card',
      '.custom-product-panel__list-card',
      '.custom-product-panel__routine article',
      '.custom-product-panel__stat',
      '.custom-product-panel__reviews article',
      '.custom-product-template-faq__item',
    ]),
    type: 'card',
    y: 16,
    stagger: 52,
  },
  {
    selector: selectorList([
      '.custom-product-panel__note',
      '.custom-product-panel__soft-note',
      '.custom-product-panel__cta-price',
      '.custom-product-panel__cta-actions',
      '.custom-product-panel__text-link',
    ]),
    type: 'copy',
    y: 12,
    stagger: 44,
  },
];

/**
 * @param {HTMLElement | null} root
 * @returns {boolean}
 */
function shouldRun(root) {
  if (!root) return false;

  const template = root.dataset.template || '';
  return template.includes('index') || template.includes('product');
}

/**
 * @param {Element} element
 * @returns {element is HTMLElement}
 */
function canReveal(element) {
  return element instanceof HTMLElement && !element.matches(EXCLUDED_SELECTOR) && !element.closest(EXCLUDED_SELECTOR);
}

/**
 * @param {HTMLElement} element
 */
function revealElement(element) {
  element.classList.add(REVEALED_CLASS);
  observer?.unobserve(element);
}

/**
 * @returns {IntersectionObserver}
 */
function getObserver() {
  if (observer) return observer;

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) revealElement(/** @type {HTMLElement} */ (entry.target));
      }
    },
    {
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.12,
    }
  );

  return observer;
}

/**
 * @param {HTMLElement} root
 * @param {{ selector: string, type: string, y: number, stagger?: number, baseDelay?: number, skipOnMobile?: boolean }} group
 * @returns {HTMLElement[]}
 */
function prepareGroup(root, group) {
  if (group.skipOnMobile && window.matchMedia('(max-width: 749px)').matches) return [];

  const elements = /** @type {HTMLElement[]} */ ([
    ...(root.matches(group.selector) ? [root] : []),
    ...root.querySelectorAll(group.selector),
  ]).filter((element) => canReveal(element) && !element.hasAttribute(PREPARED_ATTRIBUTE));

  elements.forEach((element, index) => {
    const delay = Math.min((group.baseDelay || 0) + (index % 7) * (group.stagger || 0), 320);

    element.classList.add(REVEAL_CLASS);
    element.setAttribute(PREPARED_ATTRIBUTE, group.type);
    element.style.setProperty('--wateya-reveal-y', `${group.y}px`);
    element.style.setProperty('--wateya-reveal-delay', `${delay}ms`);
  });

  return elements.filter((element) => !element.classList.contains(REVEALED_CLASS));
}

/**
 * @param {HTMLElement} root
 */
function prepareReveal(root) {
  if (!shouldRun(document.getElementById('MainContent'))) return;

  const template = document.getElementById('MainContent')?.dataset.template || '';
  const groups = template.includes('index') ? homeRevealGroups : productRevealGroups;
  const prepared = groups.flatMap((group) => prepareGroup(root, group));

  if (!prepared.length) return;

  document.documentElement.classList.add(READY_CLASS);

  if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
    prepared.forEach(revealElement);
    return;
  }

  const revealObserver = getObserver();
  prepared.forEach((element) => revealObserver.observe(element));
}

/**
 * @param {HTMLElement} root
 */
function observeMutations(root) {
  if (mutationObserver) return;

  mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement) pendingRoots.add(node);
      }
    }
    if (!pendingRoots.size || mutationFrame) return;

    mutationFrame = requestAnimationFrame(() => {
      mutationFrame = 0;
      const roots = [...pendingRoots];
      pendingRoots.clear();
      for (const addedRoot of roots) {
        if (!addedRoot.isConnected || roots.some((other) => other !== addedRoot && other.contains(addedRoot))) continue;
        prepareReveal(addedRoot);
      }
    });
  });

  mutationObserver.observe(root, { childList: true, subtree: true });
}

onDocumentReady(() => {
  const root = document.getElementById('MainContent');
  if (!shouldRun(root)) return;

  prepareReveal(root);
  observeMutations(root);

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) prepareReveal(event.target);
  });
});
